/**
 * AdminInviteEarnToggleCard — master kill-switch + per-role toggles for
 * the Invite & Earn referral feature. When OFF, the Invite & Earn card
 * disappears entirely from the relevant role's dashboard.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Spinner } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_META = [
  { key: 'vendor',            label: 'Vendor',            hint: 'Caterer, photographer, decorator, etc.' },
  { key: 'venue',             label: 'Venue Provider',    hint: 'Banquet halls, wedding lawns.' },
  { key: 'professional',      label: 'Professional',      hint: 'Chef, makeup artist, priest, etc.' },
  { key: 'entertainment_pro', label: 'Entertainment Pro', hint: 'DJ, magician, dancer, anchor, comedian.' },
];

async function fetchSettings() {
  const { data } = await axios.get(
    `${BACKEND_URL}/api/admin/invite-earn-settings`,
    { withCredentials: true },
  );
  return data;
}

async function putSettings(payload) {
  const { data } = await axios.put(
    `${BACKEND_URL}/api/admin/invite-earn-settings`,
    payload,
    { withCredentials: true },
  );
  return data;
}

const ToggleSwitch = ({ checked, onClick, disabled, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={checked}
    className={`relative inline-flex items-center h-6 w-11 rounded-full transition shrink-0 ${
      checked ? 'bg-emerald-500' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    data-testid={testid}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const AdminInviteEarnToggleCard = () => {
  const [master, setMaster] = useState(true);
  const [roles, setRoles] = useState({
    vendor: true, venue: true, professional: true, entertainment_pro: true,
  });
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null); // 'master' | role key

  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((res) => {
        if (cancelled) return;
        setMaster(res.master !== false);
        setRoles({ ...{ vendor: true, venue: true, professional: true, entertainment_pro: true }, ...(res.roles || {}) });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || 'Failed to load settings');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const persistMaster = (next) => {
    setBusyKey('master');
    putSettings({ master: next })
      .then((res) => {
        setMaster(res.master !== false);
        setRoles({ ...roles, ...(res.roles || {}) });
        toast.success(res.master ? 'Invite & Earn enabled' : 'Invite & Earn paused globally');
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to update'))
      .finally(() => setBusyKey(null));
  };

  const persistRole = (key, next) => {
    setBusyKey(key);
    putSettings({ roles: { [key]: next } })
      .then((res) => {
        setRoles({ ...roles, ...(res.roles || {}) });
        toast.success(`${key.replace('_', ' ')} — ${next ? 'enabled' : 'disabled'}`);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to update'))
      .finally(() => setBusyKey(null));
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-invite-earn-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <Gift size={20} weight="duotone" className="text-rose-600" />
          Invite &amp; Earn — Visibility Switches
        </CardTitle>
        <CardDescription>
          Master kill-switch + per-role toggles. When OFF, the Invite &amp; Earn card
          disappears from that role&apos;s dashboard entirely.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-6 text-sm text-text-secondary">Loading…</div>
        ) : (
          <>
            {/* Master switch */}
            <div
              className="flex items-center justify-between gap-3 p-3 mb-4 border-2 rounded"
              style={{
                borderColor: master ? '#86EFAC' : '#FCA5A5',
                background: master ? '#F0FDF4' : '#FEF2F2',
              }}
              data-testid="invite-earn-master-row"
            >
              <div className="flex-1">
                <div className="text-sm font-bold flex items-center gap-2">
                  Master switch
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      master ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                    }`}
                    data-testid="invite-earn-master-pill"
                  >
                    {master ? 'On' : 'Off'}
                  </span>
                </div>
                <p className="text-[11px] text-text-secondary mt-1">
                  Turns Invite &amp; Earn on / off for <strong>every</strong> role.
                  Use the per-role switches below for finer control.
                </p>
              </div>
              <ToggleSwitch
                checked={master}
                disabled={busyKey === 'master'}
                onClick={() => persistMaster(!master)}
                testid="invite-earn-master-toggle"
              />
            </div>

            {/* Per-role */}
            <div className="space-y-2">
              {ROLE_META.map((r) => {
                const checked = roles[r.key] !== false;
                const disabled = !master || busyKey === r.key;
                return (
                  <div
                    key={r.key}
                    className="flex items-center justify-between gap-3 p-3 border border-[#E8E1D5] rounded bg-[#FAF6EE]/40"
                    data-testid={`invite-earn-role-row-${r.key}`}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-bold flex items-center gap-2">
                        {r.label}
                        {!master && (
                          <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded">
                            blocked by master
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-text-secondary">{r.hint}</p>
                    </div>
                    <ToggleSwitch
                      checked={checked && master}
                      disabled={disabled}
                      onClick={() => persistRole(r.key, !checked)}
                      testid={`invite-earn-role-toggle-${r.key}`}
                    />
                    {busyKey === r.key && (
                      <Spinner size={14} className="animate-spin text-text-secondary" />
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-[11px] text-text-secondary mt-4">
              Visibility updates instantly. Users will see the change on their next dashboard load.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminInviteEarnToggleCard;
